import React, { useState } from 'react';
import { API } from '../../utils/api';
import OnboardingShell from './OnboardingShell';
import ChoiceCard from './ChoiceCard';

// Adapted from Onboarding.dc.html: the mockup's second option was "Agentic
// Systems" as a selectable primary goal. No real Agentic Systems track
// exists yet (see docs/build-plan/07-curriculum-content-plan.md -- it's
// roadmap, shown honestly as "coming soon" on Landing, not something to
// offer as a live choice here). Replaced with the applied-practice topics
// that are real today.
const GOALS = [
  { id: 'prompt-engineering', icon: 'topic-basic-prompt-engineering', title: 'Prompt Engineering fundamentals', description: 'Zero-shot, few-shot, chain-of-thought, structured output.' },
  { id: 'applied-practice', icon: 'Sparkles', title: 'Applied practice', description: 'Writing, coding, data analysis, and other real-work tasks.' },
  { id: 'unsure', icon: 'Compass', title: "Not sure -- recommend a path", description: "We'll start with fundamentals and branch from there." },
] as const;

type GoalId = typeof GOALS[number]['id'];

interface OnboardingGoalProps {
  onNext: () => void;
}

const OnboardingGoal: React.FC<OnboardingGoalProps> = ({ onNext }) => {
  const [selected, setSelected] = useState<GoalId>('prompt-engineering');
  const [saving, setSaving] = useState(false);

  const save = async (goalTrack?: GoalId) => {
    setSaving(true);
    try {
      await API.put('/onboarding', { step: 'goal', ...(goalTrack ? { goalTrack } : {}) });
    } catch {
      // Non-fatal: onboarding is a personalization nicety, not a gate --
      // don't block the user's progress on a failed save.
    } finally {
      setSaving(false);
      onNext();
    }
  };

  return (
    <OnboardingShell
      currentStep={1}
      totalSteps={2}
      heading="What do you want to get good at first?"
      subheading="You'll be able to explore everything else as you go -- pick where to start."
      onContinue={() => save(selected)}
      onSkip={() => save()}
      continueDisabled={saving}
    >
      {GOALS.map((goal) => (
        <ChoiceCard
          key={goal.id}
          icon={goal.icon}
          title={goal.title}
          description={goal.description}
          selected={selected === goal.id}
          onClick={() => setSelected(goal.id)}
        />
      ))}
    </OnboardingShell>
  );
};

export default OnboardingGoal;
